import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Cloud, Smartphone, Loader2, RefreshCw, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatDateID, formatMonths } from "@/lib/currency";

function ReportCard({ id, createdAt, score, status, emergencyMonths, testId, synced }) {
  return (
    <div className="rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-zinc-400">
          <Clock className="h-3.5 w-3.5" />
          <span className="text-xs">{formatDateID(createdAt)}</span>
        </div>
        <div className="flex items-center gap-2">
          {synced && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700"
              data-testid={`synced-badge-${id}`}
            >
              <CheckCircle2 className="h-3 w-3" /> Tersinkron
            </span>
          )}
          <StatusBadge status={status} />
        </div>
      </div>
      <div className="mt-3 flex items-end justify-between">
        <div>
          <p className="text-[11px] text-zinc-400">Skor Ketahanan</p>
          <p className="font-mono text-2xl font-black text-zinc-900">{score}</p>
        </div>
        <div className="text-right">
          <p className="text-[11px] text-zinc-400">Dana Darurat</p>
          <p className="font-mono text-lg font-bold text-zinc-700">
            {formatMonths(emergencyMonths)} bulan
          </p>
        </div>
      </div>
    </div>
  );
}

export default function History() {
  const navigate = useNavigate();
  const { history, synced } = useApp();
  const { isAuthenticated } = useAuth();
  const [cloud, setCloud] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadCloud = async () => {
    setLoading(true);
    try {
      const d = await api.myReports();
      setCloud(d.reports || []);
    } catch {
      setCloud([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) loadCloud();
    else setCloud([]);
  }, [isAuthenticated]);

  return (
    <div className="px-6 pt-8">
      <h1 className="mb-1 font-heading text-2xl font-extrabold text-zinc-900">Riwayat</h1>
      <p className="mb-6 text-sm text-zinc-400">Laporan ketahanan keluarga yang tersimpan</p>

      {/* Cloud (cross-device) */}
      {isAuthenticated ? (
        <section className="mb-6" data-testid="cloud-history-section">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-zinc-500">
              <Cloud className="h-4 w-4 text-emerald-600" />
              <span className="text-xs font-semibold uppercase tracking-wider font-heading">
                Tersimpan di akun · lintas perangkat
              </span>
            </div>
            <button
              data-testid="cloud-history-refresh"
              onClick={loadCloud}
              className="text-zinc-400 hover:text-zinc-700"
              aria-label="Muat ulang"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
            </div>
          ) : cloud.length === 0 ? (
            <div
              className="rounded-3xl border border-dashed border-zinc-200 p-6 text-center text-sm text-zinc-400"
              data-testid="cloud-history-empty"
            >
              Belum ada laporan tersimpan di akun. Gunakan "Simpan laporan" pada hasil untuk menyimpannya.
            </div>
          ) : (
            <div className="space-y-3" data-testid="cloud-history-list">
              {cloud.map((r) => (
                <ReportCard
                  key={r.id}
                  id={r.id}
                  createdAt={r.createdAt}
                  score={r.score}
                  status={r.status}
                  emergencyMonths={r.emergencyMonths}
                  testId={`cloud-history-item-${r.id}`}
                />
              ))}
            </div>
          )}
        </section>
      ) : (
        <div
          className="mb-6 rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow"
          data-testid="cloud-history-signin-prompt"
        >
          <div className="flex items-center gap-2 text-zinc-700">
            <Cloud className="h-4 w-4 text-emerald-600" />
            <p className="font-heading text-sm font-bold">Simpan lintas perangkat</p>
          </div>
          <p className="mt-1 text-xs text-zinc-500">
            Masuk untuk menyimpan laporan Anda dan membukanya dari perangkat lain.
          </p>
          <div className="mt-3 flex gap-2">
            <Button
              data-testid="cloud-history-register-btn"
              onClick={() => navigate("/register?next=/app/riwayat")}
              className="h-10 flex-1 rounded-full bg-zinc-900 text-sm font-semibold hover:bg-zinc-800"
            >
              Daftar
            </Button>
            <Button
              data-testid="cloud-history-login-btn"
              onClick={() => navigate("/login?next=/app/riwayat")}
              variant="outline"
              className="h-10 flex-1 rounded-full border-zinc-300 text-sm font-semibold"
            >
              Masuk
            </Button>
          </div>
        </div>
      )}

      {/* Local (this device) */}
      <section>
        <div className="mb-3 flex items-center gap-2 text-zinc-500">
          <Smartphone className="h-4 w-4 text-zinc-400" />
          <span className="text-xs font-semibold uppercase tracking-wider font-heading">
            Di perangkat ini
          </span>
        </div>

        {history.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-zinc-200 p-8 text-center text-sm text-zinc-400">
            Belum ada laporan tersimpan.
          </div>
        ) : (
          <div className="space-y-3" data-testid="history-list">
            {history.map((h) => (
              <ReportCard
                key={h.id}
                id={h.id}
                createdAt={h.createdAt}
                score={h.score}
                status={h.status}
                emergencyMonths={h.emergencyMonths}
                testId={`history-item-${h.id}`}
                synced={!!(synced && synced[h.id])}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
